import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { StoreBadges } from '@/components/StoreBadges';
import { DownloadAppDialog } from '@/components/DownloadAppDialog';

export default function SplashPage() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [downloadOpen, setDownloadOpen] = useState(false);

  useEffect(() => {
    if (!loading && user) nav('/', { replace: true });
  }, [user, loading, nav]);

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      <Seo
        title="Thirrsty.com — Your VIP Pass to the Night"
        description="Thirrsty.com is the social PWA dedicated to nightlife. Free profile, personal QR code for instant venue discounts, live timeline, and a USA map of every bar, club, lounge, coffee shop, vineyard, brewery, and frat party."
        path="/welcome"
      />
      <div aria-hidden className="absolute inset-0 th-hero-radial opacity-50" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/40" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col items-center px-6 py-12 text-center">
        {/* Mobile-only prominent hint: appears ABOVE the logo with a down-arrow */}
        <div
          className="sm:hidden mt-4 mb-3 flex flex-col items-center gap-1 select-none th-float-in"
          data-testid="splash-download-hint-mobile"
        >
          <span className="text-[13px] uppercase tracking-[0.28em] text-[#F6B24A] font-bold">
            Tap the logo
          </span>
          <span className="text-[16px] text-white font-semibold">
            to download the Mobile App
          </span>
          <svg
            className="mt-1 h-6 w-6 text-[#F6B24A] animate-bounce"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M12 4 V19 M5 13 L12 20 L19 13"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </div>

        <div
          className="mt-2 sm:mt-10 th-float-in flex w-full items-center justify-center gap-4"
          data-testid="splash-logo-block"
        >
          {/* Desktop-only left-side hint that points at the tappable logo */}
          <div
            className="hidden sm:flex flex-col items-end text-right leading-tight select-none flex-shrink-0 whitespace-nowrap"
            aria-hidden
            data-testid="splash-download-hint"
          >
            <span className="text-[13px] uppercase tracking-[0.24em] text-[#F6B24A] font-bold">
              Tap
            </span>
            <span className="text-[15px] text-white font-semibold">
              to Download<br />the Mobile App
            </span>
            <svg
              className="mt-1 h-3 w-10 text-[#F6B24A]"
              viewBox="0 0 24 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path d="M0 5 H21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path
                d="M18 1 L23 5 L18 9"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>

          <button
            type="button"
            onClick={() => setDownloadOpen(true)}
            aria-label="Tap to download the Thirrsty mobile app"
            data-testid="splash-logo-download-button"
            className="rounded-3xl transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B24A]/70 focus-visible:ring-offset-4 focus-visible:ring-offset-[#07070A]"
          >
            <img
              src="/logo.png"
              alt="Tap the Thirrsty.com logo to download the mobile app"
              className="h-[16rem] w-[16rem] sm:h-[20rem] sm:w-[20rem] max-w-[68vw] max-h-[68vw] rounded-3xl object-cover shadow-[0_30px_120px_rgba(246,178,74,0.35)] ring-2 ring-[#F6B24A]/25"
            />
          </button>
        </div>

        <div className="mt-6 th-float-in" style={{ animationDelay: '80ms' }}>
          <h1 className="font-display text-7xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            <span className="text-white">Thirrsty</span>
            <span className="text-[#F6B24A]">.com</span>
          </h1>
          <p className="mt-3 text-base text-white/70">Your VIP pass to the night.</p>
        </div>

        <div className="mt-10 w-full space-y-3 th-float-in" style={{ animationDelay: '160ms' }}>
          <Button
            data-testid="splash-get-started-button"
            onClick={() => nav('/auth/signup')}
            className="h-12 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] shadow-[0_0_0_1px_rgba(246,178,74,0.25),0_12px_30px_rgba(246,178,74,0.18)] font-semibold"
          >
            <Sparkles className="mr-2 h-4 w-4" /> Get Started
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            data-testid="splash-login-button"
            onClick={() => nav('/auth/login')}
            variant="outline"
            className="h-12 w-full rounded-xl border-white/12 bg-white/5 backdrop-blur-xl text-white hover:bg-white/10"
          >
            I already have an account
          </Button>
          <Button
            data-testid="splash-list-venue-button"
            onClick={() => nav('/pricing')}
            variant="ghost"
            className="h-11 w-full rounded-xl text-[#F6B24A] hover:bg-[#F6B24A]/10"
          >
            List your venue → 1st month free
          </Button>
        </div>

        <div className="mt-8 w-full th-float-in" style={{ animationDelay: '240ms' }}>
          <StoreBadges size="md" variant="row" showLabel testIdPrefix="splash-store-badges" />
        </div>

        <p className="mt-auto pt-12 text-xs text-white/40" data-testid="splash-owner-note">
          A Banks Class LLC product · 21+ where alcohol is served
        </p>
        <p className="text-center text-[11px] text-white/35 pt-1">
          <Link to="/privacy" className="hover:text-white/60 underline">Privacy</Link> · <Link to="/terms" className="hover:text-white/60 underline">Terms</Link> · <Link to="/contact" className="hover:text-white/60 underline">Contact</Link>
        </p>
      </div>

      <DownloadAppDialog open={downloadOpen} onOpenChange={setDownloadOpen} />
    </div>
  );
}
